import { alertController } from '@ionic/core/components';
import { html } from '../lib/html.js';
import { onlyDigits } from '../lib/masks.js';
import { applyApiFieldErrors, busy, clearFieldError, confirm, setFieldError, toastError, toastSuccess } from '../lib/ui.js';
import { MAX_ITEM_PHOTOS, PHOTO_POLICY, addDays, addMonths, answerText, fmtDay, fmtKm, maintenances, meterUnit, setMeterUnit, todayBR, uploadMaintenancePhoto } from '../services/maintenances.js';

const hours = () => meterUnit() === 'HOURS';
import { Page, go } from './base.js';

/**
 * /oficina/veiculos/:vehicleId/manutencao                 registrar
 * /oficina/veiculos/:vehicleId/manutencao/:maintenanceId  corrigir (nova versão, com motivo)
 *
 * Itens por tipo; a próxima troca vem sugerida pelo intervalo do tipo e pode ser ajustada.
 * Foto da peça conforme o tipo (admin): obrigatória, opcional ou não se aplica. Só pela câmera
 * (capture): nada de foto antiga da galeria. Cada foto sobe para o S3 assim que é tirada.
 * Campos adicionais do tipo (admin): texto, numérico, radio button ou seletor, obrigatórios ou não.
 */
class MaintenanceFormPage extends Page {
  template() {
    return html`
      <ion-header translucent>
        <ion-toolbar>
          <ion-buttons slot="start"><ion-back-button default-href="/home" text="Voltar"></ion-back-button></ion-buttons>
          <ion-title class="title">Registrar serviço</ion-title>
        </ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding" fullscreen>
        <div class="narrow body"></div>
        <input type="file" class="shoot" accept="image/*" capture="environment" hidden />
      </ion-content>`;
  }

  setup() {
    this.$('.body').addEventListener('click', (e) => {
      const el = e.target.closest('[data-action]');
      if (el) this.action(el.dataset.action, el);
    });
    this.$('input.shoot').addEventListener('change', (e) => {
      const file = e.target.files?.[0];
      if (file && this.shootIndex !== null) this.addPhoto(this.shootIndex, file);
    });
  }

  get vid() {
    if (this.vehicleId) return String(this.vehicleId);
    return decodeURIComponent(location.pathname.match(/\/veiculos\/([^/?#]+)/)?.[1] ?? '');
  }

  get mid() {
    if (this.maintenanceId) return String(this.maintenanceId);
    return decodeURIComponent(location.pathname.match(/\/manutencao\/([^/?#]+)/)?.[1] ?? '') || null;
  }

  async onEnter() {
    const body = this.$('.body');
    body.innerHTML = '<div class="center"><ion-spinner></ion-spinner></div>';
    this.$('.title').textContent = this.mid ? 'Corrigir manutenção' : 'Registrar serviço';
    try {
      // a lista traz a linha do veículo (category/meterUnit): os tipos vêm filtrados para ela
      const list = await maintenances.workshopList(this.vid);
      setMeterUnit(list.meterUnit);
      const types = await maintenances.types(list.category);
      this.types = types.items;
      this.list = list;
      const existing = this.mid ? list.items.find((m) => m.id === this.mid) : null;
      if (this.mid && (!existing || !existing.canCorrect)) throw new Error('Esta manutenção não pode ser corrigida pela sua empresa.');
      this.existing = existing;
      this.form = existing
        ? { serviceDate: existing.serviceDate, km: String(existing.km), notes: existing.notes ?? '', items: existing.items.map((i) => ({ ...i, nextDueKm: i.nextDueKm === null ? '' : String(i.nextDueKm), nextDueDate: i.nextDueDate ?? '', photos: (i.photos ?? []).map((ph) => ({ key: ph.id, id: ph.id, url: ph.url, state: 'done' })), extra: Object.fromEntries((i.fields ?? []).map((x) => [x.key, typeof x.value === 'number' ? String(x.value).replace('.', ',') : String(x.value)])) })) }
        : { serviceDate: todayBR(), km: '', notes: '', items: [] };
      this.render();
    } catch (err) {
      body.innerHTML = html`<div class="center"><ion-icon name="alert-circle-outline" class="empty-icon"></ion-icon><p>${err.message}</p><ion-button fill="outline" data-action="back">Voltar</ion-button></div>`.value;
    }
  }

  /** Guarda o que foi digitado antes de redesenhar. */
  collect() {
    const f = this.form;
    const val = (sel) => this.$(sel)?.value ?? '';
    f.serviceDate = String(val('ion-input[name="serviceDate"]'));
    f.km = onlyDigits(val('ion-input[name="km"]'));
    f.notes = String(val('ion-textarea[name="notes"]'));
    this.reason = String(val('ion-textarea[name="reason"]'));
    this.querySelectorAll('.item-card').forEach((card, i) => {
      const it = f.items[i];
      it.description = String(card.querySelector('ion-input[name="description"]').value ?? '');
      it.nextDueKm = onlyDigits(card.querySelector('ion-input[name="nextDueKm"]').value);
      it.nextDueDate = String(card.querySelector('ion-input[name="nextDueDate"]').value ?? '');
      for (const el of card.querySelectorAll('[data-field]')) it.extra[el.dataset.field] = String(el.value ?? '');
    });
  }

  render() {
    const f = this.form;
    const today = todayBR();
    const lastKm = this.list.currentKm;
    this.$('.body').innerHTML = html`
      <form novalidate>
        <ion-list lines="none" class="form">
          <div class="row">
            <ion-input name="serviceDate" type="date" label="Data do serviço" label-placement="stacked" fill="outline" min="${addDays(today, -30)}" max="${today}"></ion-input>
            <ion-input name="km" label="${hours() ? 'Horímetro (h)' : 'Km do veículo'}" label-placement="floating" fill="outline" inputmode="numeric" maxlength="9"
              helper-text="${lastKm === null ? 'Primeiro registro deste veículo' : this.list.kmSource === 'OWNER' ? `${hours() ? 'Informado' : 'Informada'} pelo dono: ${fmtKm(lastKm)}` : `Último registro: ${fmtKm(lastKm)}`}"></ion-input>
          </div>
          <div class="panel notice km-warn" hidden>
            <span class="row-icon warning"><ion-icon name="warning-outline"></ion-icon></span>
            <div><p></p></div>
          </div>
        </ion-list>

        <h3 class="section-title">O que foi feito</h3>
        ${f.items.map(
          (it, i) => html`
            <ion-card class="item-card">
              <ion-card-header>
                <ion-card-title>${it.typeName}</ion-card-title>
                <ion-button fill="clear" color="medium" size="small" class="item-x" data-action="remove" data-i="${i}" aria-label="Remover ${it.typeName}"><ion-icon slot="icon-only" name="trash-outline"></ion-icon></ion-button>
              </ion-card-header>
              <ion-card-content>
                <ion-list lines="none" class="form">
                  <ion-input name="description" label="${it.typeKey === 'outro' ? 'Descrição do serviço' : 'Produto / peça (opcional)'}" label-placement="floating" fill="outline" maxlength="200"></ion-input>
                  ${this.ownerSaid(it.typeKey)}
                  ${this.extraFields(it)}
                  <p class="hint small">Próxima troca (sugestão pelo tipo; ajuste se quiser)</p>
                  <div class="row">
                    <ion-input name="nextDueKm" label="${hours() ? 'Em horas' : 'Em km'}" label-placement="floating" fill="outline" inputmode="numeric" maxlength="9"></ion-input>
                    <ion-input name="nextDueDate" type="date" label="Até a data" label-placement="stacked" fill="outline"></ion-input>
                  </div>
                </ion-list>
                ${this.photoBlock(it, i)}
              </ion-card-content>
            </ion-card>`,
        )}
        <ion-button expand="block" fill="outline" data-action="add"><ion-icon slot="start" name="add-circle-outline"></ion-icon>Adicionar item</ion-button>

        <ion-list lines="none" class="form ion-margin-top">
          <ion-textarea name="notes" label="Observações (opcional)" label-placement="floating" fill="outline" auto-grow="true" maxlength="1000"></ion-textarea>
          ${this.existing ? html`<ion-textarea name="reason" label="Motivo da correção" label-placement="floating" fill="outline" auto-grow="true" maxlength="300" helper-text="Fica registrado; a versão anterior é mantida."></ion-textarea>` : ''}
        </ion-list>
        <ion-button expand="block" class="submit biz" data-action="save">${this.existing ? 'Salvar correção' : 'Salvar registro'}</ion-button>
      </form>`.value;

    // valores (propriedades, não atributos)
    this.$('ion-input[name="serviceDate"]').value = f.serviceDate;
    this.$('ion-input[name="km"]').value = f.km;
    this.$('ion-textarea[name="notes"]').value = f.notes;
    if (this.existing) this.$('ion-textarea[name="reason"]').value = this.reason ?? '';
    this.querySelectorAll('.item-card').forEach((card, i) => {
      const it = f.items[i];
      card.querySelector('ion-input[name="description"]').value = it.description ?? '';
      card.querySelector('ion-input[name="nextDueKm"]').value = it.nextDueKm ?? '';
      card.querySelector('ion-input[name="nextDueDate"]').value = it.nextDueDate ?? '';
      for (const el of card.querySelectorAll('[data-field]')) el.value = it.extra[el.dataset.field] ?? '';
    });
    for (const el of this.querySelectorAll('ion-input[name="km"], ion-input[name="nextDueKm"]')) {
      el.addEventListener('ionInput', (e) => (e.target.value = onlyDigits(e.target.value)));
    }
    // campos adicionais: numérico só aceita número (com vírgula); mexer no campo tira o erro dele
    for (const el of this.querySelectorAll('ion-input[data-number]')) {
      el.addEventListener('ionInput', (e) => (e.target.value = String(e.target.value ?? '').replace(/[^\d.,-]/g, '')));
    }
    for (const el of this.querySelectorAll('[data-field]')) {
      el.addEventListener(el.tagName === 'ION-INPUT' ? 'ionInput' : 'ionChange', () => this.fieldError(el, ''));
    }
    this.$('ion-input[name="km"]').addEventListener('ionInput', (e) => {
      f.km = onlyDigits(e.target.value);
      this.resuggest();
      this.kmWarning();
    });
    this.kmWarning();
    this.$('ion-input[name="serviceDate"]').addEventListener('ionChange', (e) => {
      f.serviceDate = String(e.target.value ?? '');
      this.resuggest();
    });
    this.querySelectorAll('.item-card').forEach((card, i) => {
      card.querySelector('ion-input[name="nextDueKm"]').addEventListener('ionInput', () => (f.items[i].autoKm = false));
      card.querySelector('ion-input[name="nextDueDate"]').addEventListener('ionChange', (e) => {
        if (String(e.target.value ?? '') !== f.items[i].nextDueDate) f.items[i].autoDate = false;
      });
    });
    this.$('form').addEventListener('submit', (e) => e.preventDefault());
  }

  /** Regra de foto do tipo: a atual do admin (tipo desativado: a gravada no item). */
  policy(it) {
    return this.types.find((t) => t.key === it.typeKey)?.photoPolicy ?? it.photoPolicy ?? 'OPTIONAL';
  }

  /** Campos adicionais do tipo, definidos no admin. Tipo desativado (só na correção): os valores gravados ficam como estão. */
  fieldDefs(it) {
    return this.types.find((t) => t.key === it.typeKey)?.fields ?? [];
  }

  /** Um controle por campo: texto, numérico, seletor ou radio button. Opcional leva "(opcional)" no rótulo. */
  extraFields(it) {
    return this.fieldDefs(it).map((d) => {
      const label = d.required ? d.label : `${d.label} (opcional)`;
      if (d.type === 'TEXT') return html`<ion-input data-field="${d.key}" label="${label}" label-placement="floating" fill="outline" maxlength="200"></ion-input>`;
      if (d.type === 'NUMBER') return html`<ion-input data-field="${d.key}" data-number label="${label}" label-placement="floating" fill="outline" inputmode="decimal" maxlength="15"></ion-input>`;
      // correção: o valor gravado continua na lista mesmo que o admin tenha tirado a opção
      const saved = it.extra[d.key];
      const options = saved && !d.options.includes(saved) ? [...d.options, saved] : d.options;
      if (d.type === 'SELECT') {
        return html`
          <ion-select data-field="${d.key}" label="${label}" label-placement="floating" fill="outline" interface="alert" cancel-text="Cancelar" ok-text="OK">
            ${d.required ? '' : html`<ion-select-option value="">Nenhum</ion-select-option>`}
            ${options.map((o) => html`<ion-select-option value="${o}">${o}</ion-select-option>`)}
          </ion-select>`;
      }
      return html`
        <div class="extra-field">
          <span class="extra-label">${label}</span>
          <ion-radio-group data-field="${d.key}" allow-empty-selection="${d.required ? 'false' : 'true'}">
            ${options.map((o) => html`<ion-radio value="${o}" label-placement="end" justify="start">${o}</ion-radio>`)}
          </ion-radio-group>
          <p class="extra-error" role="alert"></p>
        </div>`;
    });
  }

  /** Erro num campo adicional (mensagem vazia limpa). O radio button não tem texto de erro próprio: usa o parágrafo do grupo. */
  fieldError(el, message) {
    if (!el) return;
    if (el.tagName === 'ION-RADIO-GROUP') el.closest('.extra-field').querySelector('.extra-error').textContent = message;
    else if (message) setFieldError(el, message);
    else clearFieldError(el);
  }

  photoBlock(it, i) {
    const pol = PHOTO_POLICY[this.policy(it)];
    if (!pol) return '';
    const photos = it.photos ?? [];
    const required = this.policy(it) === 'REQUIRED';
    return html`
      <div class="photo-block ${required ? 'required' : ''}" data-i="${i}">
        <div class="photo-head">
          <span>Foto da peça</span>
          <span class="pill ${required ? 'warning' : ''}">${pol.label}</span>
        </div>
        ${photos.length
          ? html`<div class="thumbs">
              ${photos.map((ph) => html`
                <div class="thumb ${ph.state}">
                  <img src="${ph.url}" alt="Foto da peça" />
                  ${ph.state === 'uploading' ? html`<div class="thumb-busy"><ion-spinner name="crescent"></ion-spinner></div>` : ''}
                  <button type="button" class="thumb-x" data-action="unphoto" data-i="${i}" data-k="${ph.key}" aria-label="Remover foto">×</button>
                </div>`)}
            </div>`
          : ''}
        <p class="hint small">${pol.hint}</p>
        <p class="photo-error" role="alert"></p>
        ${photos.length < MAX_ITEM_PHOTOS
          ? html`<ion-button fill="outline" size="small" data-action="shoot" data-i="${i}"><ion-icon slot="start" name="camera-outline"></ion-icon>${photos.length ? 'Tirar outra foto' : 'Tirar foto'}</ion-button>`
          : html`<p class="hint small">Máximo de ${MAX_ITEM_PHOTOS} fotos por item.</p>`}
      </div>`;
  }

  /** Redesenha só as fotos de um item (não mexe no que está sendo digitado). */
  refreshPhotos(i) {
    const block = this.querySelectorAll('.item-card')[i]?.querySelector('.photo-block');
    if (block) block.outerHTML = this.photoBlock(this.form.items[i], i).value;
  }

  async addPhoto(i, file) {
    const it = this.form.items[i];
    if (!it) return;
    it.photos ??= [];
    if (it.photos.length >= MAX_ITEM_PHOTOS) return;
    const ph = { key: `l${Date.now()}${Math.random().toString(36).slice(2, 6)}`, id: null, url: URL.createObjectURL(file), state: 'uploading' };
    it.photos.push(ph);
    this.refreshPhotos(i);
    try {
      ph.id = (await uploadMaintenancePhoto(this.vid, file)).id;
      ph.state = 'done';
    } catch (err) {
      it.photos.splice(it.photos.indexOf(ph), 1);
      toastError(err.message);
    }
    this.refreshPhotos(this.form.items.indexOf(it));
  }

  async action(name, el) {
    if (name === 'back') return go(`/oficina/veiculos/${this.vid}`, 'back');
    if (name === 'shoot') {
      this.shootIndex = Number(el.dataset.i);
      const input = this.$('input.shoot');
      input.value = '';
      return input.click();
    }
    if (name === 'unphoto') {
      const it = this.form.items[Number(el.dataset.i)];
      it.photos = (it.photos ?? []).filter((ph) => ph.key !== el.dataset.k);
      return this.refreshPhotos(Number(el.dataset.i));
    }
    this.collect();
    if (name === 'remove') {
      this.form.items.splice(Number(el.dataset.i), 1);
      return this.render();
    }
    if (name === 'add') return this.addItem();
    if (name === 'save') return this.save(el);
  }

  async addItem() {
    const used = new Set(this.form.items.map((i) => i.typeKey));
    const options = this.types.filter((t) => t.key === 'outro' || !used.has(t.key));
    const a = await alertController.create({
      header: 'Adicionar item',
      inputs: options.map((t, i) => ({ type: 'radio', label: t.name, value: t.key, checked: i === 0 })),
      buttons: [{ text: 'Cancelar', role: 'cancel' }, { text: 'Adicionar', role: 'confirm' }],
    });
    await a.present();
    const { role, data } = await a.onDidDismiss();
    if (role !== 'confirm' || !data?.values) return;
    const t = this.types.find((x) => x.key === data.values);
    // sugestão pelo intervalo do tipo; acompanha a km/data até o mecânico editar o campo
    const item = { typeKey: t.key, typeName: t.name, description: '', nextDueKm: '', nextDueDate: '', photos: [], extra: {}, autoKm: Boolean(t.intervalMeter ?? t.intervalKm), autoDate: Boolean(t.intervalMonths) };
    this.form.items.push(item);
    this.suggest(item);
    this.render();
  }

  /** Próxima troca = km/data do serviço + intervalo do tipo (só nos campos ainda automáticos). */
  suggest(item) {
    const t = this.types.find((x) => x.key === item.typeKey);
    if (!t) return;
    const km = Number(this.form.km);
    const interval = t.intervalMeter ?? t.intervalKm;
    if (item.autoKm) item.nextDueKm = km && interval ? String(km + interval) : '';
    if (item.autoDate) item.nextDueDate = this.form.serviceDate && t.intervalMonths ? addMonths(this.form.serviceDate, t.intervalMonths) : '';
  }

  /** Km ou data mudou: refaz as sugestões e atualiza os campos na tela. */
  resuggest() {
    this.querySelectorAll('.item-card').forEach((card, i) => {
      const it = this.form.items[i];
      if (!it) return;
      this.suggest(it);
      if (it.autoKm) card.querySelector('ion-input[name="nextDueKm"]').value = it.nextDueKm;
      if (it.autoDate) card.querySelector('ion-input[name="nextDueDate"]').value = it.nextDueDate;
    });
  }

  /**
   * Km menor que a informada pelo dono (enquanto ela vale): só avisa, não trava.
   * Salvando, a km da oficina passa a valer.
   */
  lowerThanOwner() {
    const o = this.list.ownerKm;
    return !this.existing && this.list.kmSource === 'OWNER' && o && this.form.km !== '' && Number(this.form.km) < o.km ? o : null;
  }

  kmWarning() {
    const box = this.$('.km-warn');
    const o = this.lowerThanOwner();
    box.hidden = !o;
    if (o) box.querySelector('p').innerHTML = html`<strong>Menor que ${hours() ? 'o horímetro informado' : 'a km informada'} pelo dono</strong> (${fmtKm(o.km)} em ${fmtDay(o.date)}). Confira o painel. Se estiver certo, pode salvar: ${hours() ? 'o horímetro registrado' : 'a km registrada'} pela empresa passa a valer.`.value;
  }

  /** O que o dono informou sobre este item nos dados iniciais (ajuda a conferir). */
  ownerSaid(typeKey) {
    const it = (this.list.initialData ?? []).flatMap((d) => d.items).find((i) => i.typeKey === typeKey);
    return it ? html`<p class="owner-said"><ion-icon name="person-outline"></ion-icon>Dono informou: última troca ${answerText(it)}</p>` : '';
  }

  async save(button) {
    const f = this.form;
    const kmInput = this.$('ion-input[name="km"]');
    if (!f.km) return setFieldError(kmInput, hours() ? 'Informe o horímetro da máquina' : 'Informe a km do veículo');
    if (!f.items.length) return toastError('Adicione pelo menos um item.');
    if (this.existing && this.reason.trim().length < 5) return setFieldError(this.$('ion-textarea[name="reason"]'), 'Explique o que está sendo corrigido');
    if (f.items.some((it) => (it.photos ?? []).some((ph) => ph.state === 'uploading'))) return toastError('Aguarde as fotos terminarem de enviar.');
    const cards = this.querySelectorAll('.item-card');
    let missing = null;
    f.items.forEach((it, i) => {
      const err = cards[i]?.querySelector('.photo-error');
      if (err) err.textContent = '';
      if (this.policy(it) === 'REQUIRED' && !(it.photos ?? []).some((ph) => ph.state === 'done')) {
        if (err) err.textContent = `Tire a foto da peça (${it.typeName}).`;
        missing ??= cards[i];
      }
    });
    if (missing) {
      missing.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return toastError('Falta a foto da peça. Ela é obrigatória para este serviço.');
    }
    // campos adicionais do tipo: obrigatório preenchido e numérico com número
    let invalid = null;
    f.items.forEach((it, i) => {
      for (const d of this.fieldDefs(it)) {
        const value = (it.extra[d.key] ?? '').trim();
        const message = !value ? (d.required ? 'Obrigatório' : '') : d.type === 'NUMBER' && !Number.isFinite(Number(value.replace(',', '.'))) ? 'Informe um número' : '';
        const el = cards[i]?.querySelector(`[data-field="${CSS.escape(d.key)}"]`);
        this.fieldError(el, message);
        if (message) invalid ??= el;
      }
    });
    if (invalid) {
      invalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return toastError('Confira os campos destacados.');
    }
    const low = this.lowerThanOwner();
    if (low && !(await confirm({ header: hours() ? 'Horímetro menor que o do dono' : 'Km menor que a do dono', message: `O dono informou ${fmtKm(low.km)} em ${fmtDay(low.date)}. Salvar com ${fmtKm(f.km)}?`, confirmText: 'Salvar mesmo assim', cancelText: 'Conferir' }))) return kmInput.setFocus?.();

    const body = {
      serviceDate: f.serviceDate,
      km: Number(f.km),
      items: f.items.map((it) => ({
        typeKey: it.typeKey,
        fields: this.fieldDefs(it).map((d) => ({ key: d.key, value: (it.extra[d.key] ?? '').trim() })).filter((x) => x.value),
        ...(it.description.trim() ? { description: it.description.trim() } : {}),
        ...(it.nextDueKm ? { nextDueKm: Number(it.nextDueKm) } : {}),
        ...(it.nextDueDate ? { nextDueDate: it.nextDueDate } : {}),
        ...(this.policy(it) !== 'NONE' && it.photos?.length ? { photos: it.photos.filter((ph) => ph.state === 'done').map((ph) => ph.id) } : {}),
      })),
      ...(f.notes.trim() ? { notes: f.notes.trim() } : {}),
      ...(this.existing ? { version: this.existing.version, reason: this.reason.trim() } : {}),
    };
    await busy(button, async () => {
      try {
        if (this.existing) await maintenances.correct(this.vid, this.existing.id, body);
        else await maintenances.create(this.vid, body);
        toastSuccess(this.existing ? 'Correção salva. A versão anterior foi mantida.' : 'Manutenção registrada!');
        go(`/oficina/veiculos/${this.vid}`, 'back');
      } catch (err) {
        if (err.code === 'KM_TOO_LOW' || err.code === 'KM_TOO_HIGH') return setFieldError(kmInput, err.message);
        if (err.code === 'DATE_IN_FUTURE' || err.code === 'DATE_TOO_OLD') return setFieldError(this.$('ion-input[name="serviceDate"]'), err.message);
        if (Array.isArray(err.details)) {
          // itens: "items[0].nextDueKm" → campo do cartão; "items[0].fields.<key>" → campo adicional
          let shown = false;
          for (const d of err.details) {
            const x = /^items\[(\d+)\]\.fields\.(\w+)$/.exec(d.path);
            const extra = x && this.querySelectorAll('.item-card')[Number(x[1])]?.querySelector(`[data-field="${CSS.escape(x[2])}"]`);
            if (extra) {
              this.fieldError(extra, d.message);
              shown = true;
              continue;
            }
            const m = /^items\[(\d+)\]\.(\w+)$/.exec(d.path);
            const card = m && this.querySelectorAll('.item-card')[Number(m[1])];
            if (card && m[2] === 'photos') {
              card.querySelector('.photo-error').textContent = d.message;
              shown = true;
              continue;
            }
            const input = card?.querySelector(`ion-input[name="${m[2]}"]`);
            if (input) setFieldError(input, d.message), (shown = true);
          }
          if (shown) return toastError('Confira os itens destacados.');
          if (applyApiFieldErrors(this, err.details)) return;
        }
        toastError(err.message);
      }
    });
  }
}

customElements.define('page-maintenance-form', MaintenanceFormPage);
